import React from 'react';
import {
  EditOutlined,
  ExclamationCircleFilled,
  MinusOutlined,
} from '@ant-design/icons';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import {
  isBilling2023Subscription,
  isBilling2023V2Subscription,
  isBilling2025Subscription,
  SubscriptionModel,
} from '@mm/state/model/SubscriptionModel';
import OveragesManagement from '../../OveragesManagement/OveragesManagement';
import { useXRayBillingLimit } from './use-xray-billing-limit';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { Button } from '@mm/components';

export const BillingUserLimit = ({
  limitType,
  subscription,
  facebookPageId,
  retrieveSubscription,
  withOvergaes,
}: {
  subscription: SubscriptionModel;
  facebookPageId: number;
  retrieveSubscription: () => void;
  limitType: 'subscription' | 'page';
  withOvergaes?: boolean;
}) => {
  const facebookPages = useSelector(
    (state: StoreState) => state.connectedPage.basicPagesList,
  );
  const company = useSelector(activeCompanySelector);
  const facebookPage = facebookPages.find(
    p => p.id === facebookPageId,
  ) as EnhancedBillingPage;
  const isSubscriptionLimit = limitType === 'subscription';

  const { retrieveBasicListByCompany } = useConnectedPageCRUDActions();

  const onUpdateSuccess = () => {
    retrieveSubscription();
    if (company?.id) {
      retrieveBasicListByCompany(company.id, { withSubscription: true });
    }
  };
  const { updateXRayLimit } = useXRayBillingLimit({
    limitType,
    facebookPage,
    subscription,
    onSuccess: onUpdateSuccess,
  });

  const title = isSubscriptionLimit
    ? 'X-Ray Pixel Capture Company Limit'
    : 'X-Ray Pixel Capture Limit';

  const isBilling2023Plan = isBilling2023Subscription(subscription);
  const isBilling2023V2Plan = isBilling2023V2Subscription(subscription);
  const isBilling2025Plan = isBilling2025Subscription(subscription);
  const limitByType = isSubscriptionLimit
    ? subscription.xrayLimit
    : facebookPage?.settings?.xrayLimit;
  const hasLimit = !!limitByType;
  const limit = hasLimit
    ? `${limitByType} contacts/${
        subscription.billingPeriodLength === 'monthly' ? 'mo' : 'year'
      }`
    : 'no limit';

  const buttonComponent = facebookPage.xrayLockedAt ? (
    <Button
      onClick={updateXRayLimit}
      css={{ container: 'billing-page-card__raise-limit-btn' }}
    >
      Raise Limit
    </Button>
  ) : (
    <EditOutlined
      className="billing-page-card__edit-limit"
      onClick={updateXRayLimit}
    />
  );

  return (
    <div className="billing-page-card__limit-options-wrapper">
      {withOvergaes && !isBilling2023V2Plan && (
        <OveragesManagement
          facebookPage={facebookPage}
          subscription={subscription}
          hideQuestionIcon
          className="billing-page-card__overages"
          disableOverages={subscription.readonlyOverages}
          textOverride="Automatically charge me for overages after current allotment is exceeded.
      You will receive an email and your credit card will be charged for each overage."
        />
      )}
      {isBilling2023Plan && (
        <div className="billing-page-card__limit-wrapper">
          {' '}
          {hasLimit && (
            <ExclamationCircleFilled className="billing-page-card__exclamation-circule" />
          )}
          <div className="billing-page-card__label">
            <div>
              {title} <MinusOutlined /> {limit}
            </div>
            {buttonComponent}
          </div>{' '}
        </div>
      )}
    </div>
  );
};
